import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import type { MailVarContents } from '@adornis/mails/db/mail-template.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ComponentParamaterType, ComponentType, MessageType } from '@adornis/whatsapp/server/types.js';
import { bhBaseQlContext } from '../bh-context.js';
import { Certificate, CertificateFile } from '../entities/certificate.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import {
  getWhatsappTemplate,
  InstitutionMailTemplate,
  MailTemplateType,
  MailTemplateVars,
} from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { Product } from '../entities/products.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { TextMultipleLangItem } from '../entities/TextMultipleLangItem.js';
import { User } from '../entities/user.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsapp } from './_helper.js';

export async function notifyNuzterTeilnahme(participationID: string, withCertificate: boolean) {
  const participation = await getByID(
    Participation,
    participationID,
  )({
    _id: 1,
    _class: 1,
    userID: 1,
    productID: 1,
    product: { _id: 1, name: 1, institutionID: 1 },
    user: {
      _id: 1,
      title: 1,
      firstName: 1,
      lastName: 1,
      dateOfBirth: 1,
      email: 1,
      whatsappNotification: 1,
      whatsappPhone: 1,
    },
  });
  if (!participation) throw new Error('Couldnt find participation with ID ' + participationID);

  const user = participation.user;
  if (!user) throw new Error('no user is set');

  const product = await getByID(
    Product,
    participation.productID,
  )({
    _id: 1,
    _class: 1,
    name: 1,
    description: 1,
    sortingWeight: 1,
    hasPrice: 1,
    categoryID: 1,
    institutionID: 1,
    timeframeID: 1,
    lastReminderMailSent: 1,
    publishDate: 1,
    topic: 1,
    language: 1,
    presenceType: 1,
    isCanceled: 1,
    appointments: {
      _class: 1,
      _id: 1,
      start: 1,
      end: 1,
      isNonBlocking: 1,
      location: 1,
      name: 1,
    },
    capacity: 1,
    hideCapacity: 1,
    attachedCertificate: { certificateID: 1, descriptionsHTML: TextMultipleLangItem.allFields, exportDate: 1 },
    settings: {
      autoSendCertifatesEnabled: 1,
      evaluationLink: 1,
      inheritedEvaluationLink: 1,
      evaluationLinkCode: 1,
      inheritedEvaluationLinkCode: 1,
    },
    translatedDescriptionHTML: TextMultipleLangItem.allFields,
  });
  if (!product) throw new Error('product not found ' + participation.productID);

  let certificateServeLink = '';
  if (withCertificate) {
    const certificateFile = await Certificate.generateCertificateFileFromParticipation(product, user);

    certificateServeLink = A.absoluteUrl(
      `${new CertificateFile(certificateFile).getServeLink()}/${encodeURIComponent(
        participation.product?.name.replace(/\W/g, '_') ?? (participation.productID as string) ?? participation._id,
      )}_certificate.pdf`,
    );

    if (!certificateServeLink) {
      logger.error({ productID: participation.productID }, 'Certificate Serve Link could not be generated');
      throw new Error('Certificate Serve Link could not be generated');
    }
  }

  const institutionID = participation.product?.institutionID;

  // Get institution with color definitions
  const institution = await getByID(
    Institution,
    institutionID,
  )({
    _id: 1,
    name: 1,
    colorDefintions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplateType = withCertificate
    ? MailTemplateType.NUTZER_TEILNAHME
    : MailTemplateType.NUTZER_TEILNAHME_OHNE_ZERTIFIKAT;
  const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    institutionID,
    mailTemplateType,
  )(InstitutionMailTemplate.allFields);

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const notificationType = getNotificationType(user, mailTemplate);
  if (notificationType === 'whatsapp') {
    await sendWhatsapp({
      to: user.whatsappPhone!,
      type: MessageType.TEMPLATE,
      content: {
        template: {
          name: getWhatsappTemplate(mailTemplateType).name,
          language: {
            code: 'de',
          },
          components: [
            {
              type: ComponentType.BODY,
              parameters: [
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapFirstNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapLastNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: participation.product?.name || '-',
                },
              ],
            },
            ...(withCertificate
              ? [
                  {
                    type: ComponentType.HEADER,
                    parameters: [
                      {
                        type: ComponentParamaterType.DOCUMENT,
                        document: { link: certificateServeLink },
                      },
                    ],
                  },
                ]
              : []),
          ],
        },
      },
    });
  } else if (notificationType === 'email') {
    const mailVars = {
      [MailTemplateVars.TITLE]: participation.user?.title ?? '',
      [MailTemplateVars.FIRST_NAME]: participation.user?.firstName,
      [MailTemplateVars.LAST_NAME]: participation.user?.lastName,
      [MailTemplateVars.EVENT_TITLE]: participation.product?.name,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
      [MailTemplateVars.EVALUATION_LINK]:
        product.settings.evaluationLink ?? product.settings.inheritedEvaluationLink?.[1] ?? '',
      [MailTemplateVars.EVALUATION_CODE]:
        product.settings.evaluationLinkCode ?? product.settings.inheritedEvaluationLinkCode?.[1] ?? '',
    } as MailVarContents;

    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    const fullEmailRec = {
      email: user.email,
      institutionID,
      content,
      subject,
      attachmentPaths: [] as string[],
    };

    if (withCertificate) fullEmailRec.attachmentPaths = [certificateServeLink];

    await sendInstitutionEmail(fullEmailRec);
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}

// --- Mail Template Definition ---
defineMailTemplate(MailTemplateType.NUTZER_TEILNAHME, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Nutzer-Teilnahme mit Zertifikat',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>vielen Dank f&uuml;r den Besuch der Veranstaltung "{{event-title}}". Wir schicken Ihnen beigef&uuml;gt Ihren Teilnahmenachweis zu.</p>
<p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>thank you for attending the event&nbsp; "{{event-title}}". We are enclosing your attendance certificate.</p>
<p>We look forward to welcoming you to one of our events in the future and recommand our lecture and workshop programme at <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de</a> for this purpose</p>
<p>Best regards</p>`,
    footer: '[footer]',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.NUTZER_TEILNAHME),
  attachments: [],
  displayName: 'Teilnahmebestätigung',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.NUTZER_TEILNAHME),
});

defineMailTemplate(MailTemplateType.NUTZER_TEILNAHME_OHNE_ZERTIFIKAT, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Nutzer-Teilnahme ohne Zertifikat',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>vielen Dank f&uuml;r den Besuch der Veranstaltung "{{event-title}}".</p>
<p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>thank you for attending the event&nbsp; "{{event-title}}".</p>
<p>We look forward to welcoming you to one of our events in the future and recommand our lecture and workshop programme at <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de</a> for this purpose</p>
<p>Best regards</p>`,
    footer: '[footer]',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.NUTZER_TEILNAHME),
  attachments: [],
  displayName: 'Teilnahmebestätigung ohne Zertifikat',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.NUTZER_TEILNAHME_OHNE_ZERTIFIKAT),
});
